import { useEffect, useState } from "react";

export type Branding = {
  product_name: string;
  legal_name: string;
  short_name: string;
  support_email: string;
  theme: {
    color: string;
    logo_url: string;
    logo_square_url: string;
    favicon_url: string;
    landing_background_url: string;
    landing_headline: string;
    landing_subcopy: string;
  };
};

const fallbackBranding: Branding = {
  product_name: "Finexa",
  legal_name: "Finexa",
  short_name: "Finexa",
  support_email: "support@finexa.local",
  theme: {
    color: "#0f172a",
    logo_url: "/images/finexa-logo.svg",
    logo_square_url: "/images/finexa-mark.svg",
    favicon_url: "/favicon.ico",
    landing_background_url: "/images/bg_landing.png",
    landing_headline: "Banking infrastructure for modern financial products",
    landing_subcopy:
      "Accounts, compliance, trust services, FX, crypto rails, and ledgered money movement in one tenant-ready platform.",
  },
};

let cachedBranding: Branding | null = null;

export async function loadBranding(): Promise<Branding> {
  if (cachedBranding) return cachedBranding;

  try {
    const response = await fetch("/xapi/branding", {
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) throw new Error("branding unavailable");

    const json = await response.json();
    cachedBranding = {
      ...fallbackBranding,
      ...(json.branding ?? {}),
      theme: {
        ...fallbackBranding.theme,
        ...(json.branding?.theme ?? {}),
      },
    };

    return cachedBranding;
  } catch {
    return fallbackBranding;
  }
}

export function useBranding(): Branding {
  const [branding, setBranding] = useState<Branding>(cachedBranding ?? fallbackBranding);

  useEffect(() => {
    let alive = true;

    loadBranding().then((next) => {
      if (!alive) return;
      setBranding(next);
      document.title = next.product_name;

      const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]');
      if (favicon) favicon.href = next.theme.favicon_url;
    });

    return () => {
      alive = false;
    };
  }, []);

  return branding;
}
